'use client';

/**
 * Admin-preview banner (#417).
 *
 * "View as in Portal" on a recipient row primes a REAL portal session in the
 * admin's own browser storage, so the portal that opens is indistinguishable
 * from the employee's own — nothing said whose account was on screen and there
 * was no way back out. This banner is the affordance: it renders only when
 * `portalAuthApi.getAdminPreview()` returns a marker (a genuine recipient login
 * never writes one), names the impersonated recipient, and offers an exit
 * control that clears the portal session and returns to admin.
 *
 * The session is now genuinely read-only as well as labelled. `direct-login`
 * mints a portal JWT carrying `preview: true`; the portal's play-token route
 * turns that into the preview play token HFv3 already had, which
 * `lms-scorm-init.controller.ts` refuses to persist CMI for, and
 * `PortalPreviewReadOnlyGuard` rejects the writes that go through the portal
 * JWT directly. Reads are untouched — seeing the employee's portal is the
 * point of the feature.
 */

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { ShieldAlert } from 'lucide-react';
import {
  portalAuthApi,
  PORTAL_PREVIEW_RETURN_PATH,
  PORTAL_SESSION_EVENT,
  type PortalAdminPreview,
} from '@/lib/api/portal-auth';
import { useTranslation } from '@/lib/i18n';
import { richText } from '@/components/user-portal/learner-i18n';

export function AdminPreviewBanner() {
  const router = useRouter();
  const { t } = useTranslation();
  // localStorage is unreadable during SSR/first paint — resolve on mount.
  const [preview, setPreview] = useState<PortalAdminPreview | null>(null);

  useEffect(() => {
    const readPreview = () => setPreview(portalAuthApi.getAdminPreview());
    readPreview();
    window.addEventListener(PORTAL_SESSION_EVENT, readPreview);
    window.addEventListener('storage', readPreview);
    return () => {
      window.removeEventListener(PORTAL_SESSION_EVENT, readPreview);
      window.removeEventListener('storage', readPreview);
    };
  }, []);

  if (!preview) return null;

  const label = preview.recipientName
    ? `${preview.recipientName} (${preview.recipientEmail})`
    : preview.recipientEmail;

  const handleExit = () => {
    portalAuthApi.clearSession();
    setPreview(null);
    router.push(PORTAL_PREVIEW_RETURN_PATH);
  };

  return (
    <div
      data-testid="portal-admin-preview-banner"
      role="status"
      aria-live="polite"
      // Deliberately NOT themed from the portal CSS vars — this is admin
      // chrome and must stay visible whatever palette the workspace picked.
      className="sticky top-0 z-50 flex flex-wrap items-center justify-between gap-3 border-b border-amber-500/40 bg-amber-100 px-6 py-2 text-sm text-amber-950"
    >
      <span className="flex items-center gap-2">
        <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>
          {richText(t('portal.adminPreview.message'), {
            name: <strong className="font-semibold">{label}</strong>,
          })}
        </span>
      </span>
      <button
        type="button"
        onClick={handleExit}
        className="shrink-0 rounded-md border border-amber-600/50 px-3 py-1 font-medium hover:bg-amber-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-700"
      >
        {t('portal.adminPreview.exit')}
      </button>
    </div>
  );
}
